import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import Topbar from "@/components/Topbar";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ChatCircleDots, Phone, ArrowDownLeft, ArrowUpRight } from "@phosphor-icons/react";
import { commsApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const statusCls = {
  sent: "bg-success/15 text-success border-success/30",
  delivered: "bg-success/15 text-success border-success/30",
  received: "bg-success/15 text-success border-success/30",
  queued: "bg-muted text-foreground border-border",
  simulated: "bg-muted text-muted-foreground border-border",
  failed: "bg-destructive/15 text-destructive border-destructive/30",
};

const classCls = {
  paid: "bg-success text-white",
  promise: "bg-amber-500 text-white",
  dispute: "bg-destructive text-destructive-foreground",
  query: "bg-muted text-foreground border border-border",
  other: "bg-muted text-muted-foreground border border-border",
};

export default function CommunicationsPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => { commsApi.list().then(setItems); }, []);

  const filtered = filter === "all" ? items
    : filter === "inbound" ? items.filter(i => i.direction === "inbound")
    : filter === "outbound" ? items.filter(i => i.direction === "outbound")
    : items.filter(i => i.classification === filter);

  return (
    <AppLayout>
      <Topbar title="Communications" subtitle={`${items.length} messages & calls logged`} />
      <div className="px-6 lg:px-10 py-8">
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          {[
            ["all", "All"], ["outbound", "Outbound"], ["inbound", "Inbound"],
            ["paid", "Paid"], ["promise", "Promise"], ["dispute", "Dispute"], ["query", "Query"],
          ].map(([k, label]) => (
            <button
              key={k}
              data-testid={`comm-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                filter === k ? "bg-primary text-primary-foreground border-primary" : "bg-card text-foreground border-border hover:bg-muted"
              }`}
            >
              {label.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="border border-border bg-card rounded-md overflow-hidden" data-testid="communications-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Direction</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>AI Verdict</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-12 text-center text-sm text-muted-foreground">No communications in this view.</TableCell></TableRow>
              )}
              {filtered.map((c, idx) => (
                <TableRow key={c.id} className="stagger-in" style={{ animationDelay: `${idx * 20}ms` }} data-testid={`comm-row-${c.id}`}>
                  <TableCell className="font-mono text-xs text-muted-foreground">{formatDateTime(c.created_at)}</TableCell>
                  <TableCell>
                    {c.direction === "inbound" ? (
                      <div className="flex items-center gap-1 text-xs text-success">
                        <ArrowDownLeft className="h-3.5 w-3.5" weight="bold" /> IN
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <ArrowUpRight className="h-3.5 w-3.5" weight="bold" /> OUT
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-sm">
                      {c.channel === "whatsapp" ? <ChatCircleDots className="h-4 w-4 text-success" weight="duotone" /> : <Phone className="h-4 w-4 text-primary" weight="duotone" />}
                      <span className="capitalize">{c.channel}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{c.customer_name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{c.customer_phone}</div>
                  </TableCell>
                  <TableCell>
                    {c.classification ? (
                      <div>
                        <Badge className={`${classCls[c.classification]} font-mono text-[10px] uppercase`} data-testid={`class-badge-${c.id}`}>
                          {c.classification}
                        </Badge>
                        {c.classification_reason && (
                          <div className="text-[11px] text-muted-foreground mt-1 max-w-[200px] line-clamp-2">{c.classification_reason}</div>
                        )}
                      </div>
                    ) : (
                      <Badge variant="secondary" className="font-mono text-[10px] uppercase">{c.tone}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="max-w-md">
                    <div className="text-sm text-foreground/90 line-clamp-2">{c.message}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${statusCls[c.status] || statusCls.queued} border font-mono text-[10px] uppercase`}>
                      {c.simulated && c.direction === "outbound" ? "SIMULATED" : c.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </AppLayout>
  );
}
